import { useRef, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'motion/react';
import {
  ArrowRight,
  BadgeCheck,
  ClipboardList,
  FileSearch,
  Gauge,
  MessageSquareQuote,
  Quote,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { SitePage } from './SiteChrome';
import { useI18n } from '@/lib/i18n';
import { useSession } from '@/lib/session';
import { ButtonLink } from '@/ui';
import { Reveal } from './reveal';
import { SectionMark, Spotlight, VoidMark } from './LandingParts';
import { DemoAccess } from './DemoAccess';
import { Mark } from '@/brand/Mark';
import { trackClick } from './metrics';

/**
 * The public home page — "The Exploded View" (design-concept-v2.html,
 * approved). The hero is a two-column layout where the live instrument
 * (Instrument.tsx) is the product itself, mid-scroll; the copy beside it is
 * annotation, not the other way around. Everything below the hero keeps its
 * existing rhythm-based section structure (see site.css's file header for
 * why no section — including this one — sets its own background).
 */
export default function Landing() {
  const { t } = useI18n();
  const { config } = useSession();
  const location = useLocation();

  // The process timeline fills as it scrolls past.
  const howRef = useRef<HTMLOListElement | null>(null);
  const { scrollYProgress } = useScroll({ target: howRef, offset: ['start 85%', 'end 65%'] });
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.4 });

  const steps = [
    { icon: <ScrollText size={18} />, title: t.site.how.s1Title, body: t.site.how.s1 },
    { icon: <Gauge size={18} />, title: t.site.how.s2Title, body: t.site.how.s2 },
    { icon: <Users size={18} />, title: t.site.how.s3Title, body: t.site.how.s3 },
    { icon: <FileSearch size={18} />, title: t.site.how.s4Title, body: t.site.how.s4 },
    { icon: <BadgeCheck size={18} />, title: t.site.how.s5Title, body: t.site.how.s5 },
  ];

  const features = [
    { icon: <Gauge size={18} />, title: t.site.features.f1Title, body: t.site.features.f1 },
    { icon: <Quote size={18} />, title: t.site.features.f2Title, body: t.site.features.f2 },
    { icon: <MessageSquareQuote size={18} />, title: t.site.features.f3Title, body: t.site.features.f3 },
    { icon: <ShieldCheck size={18} />, title: t.site.features.f4Title, body: t.site.features.f4 },
    { icon: <ClipboardList size={18} />, title: t.site.features.f5Title, body: t.site.features.f5 },
    { icon: <Sparkles size={18} />, title: t.site.features.f6Title, body: t.site.features.f6 },
  ];

  const shots = [
    { src: '/shots/matrix.webp', title: t.site.showcase.m1Title, body: t.site.showcase.m1 },
    { src: '/shots/evidence.webp', title: t.site.showcase.m2Title, body: t.site.showcase.m2 },
  ];

  const problems = [t.site.problem.p1Title, t.site.problem.p2Title, t.site.problem.p3Title, t.site.problem.p4Title];

  const audience = [
    { icon: <ClipboardList size={18} />, title: t.site.audience.a1Title },
    { icon: <ShieldCheck size={18} />, title: t.site.audience.a2Title },
    { icon: <Gauge size={18} />, title: t.site.audience.a3Title },
    { icon: <BadgeCheck size={18} />, title: t.site.audience.a4Title },
  ];

  const demoButton = (label: string) => (config?.demo !== false ? <DemoAccess label={label} /> : null);

  // The headline highlights one word (the brand's evidence claim) in the
  // accent colour — a plain word match against the translated string, so it
  // still works in German, where the highlighted word is inflected
  // ("Nachweisen") but still appears verbatim in the title.
  const titleWords = t.site.hero.title.split(/(\s+)/);
  const highlightKey = t.site.hero.highlight.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

  return (
    <SitePage>
      <section className="hero">
        <div className="site-shell hero-inner">
          <div className="hero-copy">
            <Reveal>
              <p className="hero-kicker">
                <i aria-hidden="true" />
                {t.site.hero.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.085}>
              <h1 className="hero-title">
                {titleWords.map((word, idx) => {
                  const key = word.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
                  return key && key === highlightKey ? (
                    <em key={idx} className="hero-title-highlight">{word}</em>
                  ) : (
                    <span key={idx}>{word}</span>
                  );
                })}
              </h1>
            </Reveal>
            <Reveal delay={0.17}>
              <p className="hero-lead">{t.site.hero.lead}</p>
            </Reveal>
            <Reveal delay={0.255}>
              <div className="hero-actions">
                <ButtonLink
                  to="/contact"
                  variant="primary"
                  size="lg"
                  iconRight={<ArrowRight size={18} />}
                  onClick={() => trackClick('hero_start', location.pathname)}
                >
                  {t.site.hero.ctaPrimary}
                </ButtonLink>
                {demoButton(t.site.hero.ctaSecondary)}
              </div>
            </Reveal>
            {config?.demo !== false ? (
              <Reveal delay={0.34}>
                <p className="hero-note numeric">{t.site.hero.note}</p>
              </Reveal>
            ) : null}
          </div>

          {/* A real screen from the product, not an illustration: the filed
              ranking at Stage 5, captured from the same recording the product
              film is cut from. Sized at its natural 1440x810 so the browser
              reserves the box before it loads and the hero does not reflow. */}
          <Reveal delay={0.17} className="hero-rig">
            <figure className="hero-shot">
              <div className="shot-frame">
                <span className="shot-chrome" aria-hidden="true" />
                <img
                  src="/shots/decision.webp"
                  width={1440}
                  height={810}
                  alt={t.site.hero.shotCaption}
                  decoding="async"
                />
              </div>
              <figcaption>{t.site.hero.shotCaption}</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="site-section problem-section">
        <div className="site-shell">
          <Reveal>
            <SectionMark index={1} total={5} />
            <h2 className="section-title">{t.site.problem.title}</h2>
          </Reveal>
          <div className="problem-grid">
            {problems.map((problem, i) => (
              <Reveal key={problem} delay={i * 0.085}>
                <Spotlight className="problem-card">
                  <VoidMark />
                  <h3>{problem}</h3>
                </Spotlight>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="site-section how-section" id="how">
        <div className="site-shell">
          <Reveal>
            <div className="section-head">
              <SectionMark index={2} total={5} />
              <h2 className="section-title">{t.site.how.title}</h2>
            </div>
          </Reveal>
          <div className="how-wrap">
            <span className="how-rail" aria-hidden="true">
              <motion.span className="how-rail-fill" style={{ scaleX: progress }} />
            </span>
            <ol className="how-list" ref={howRef}>
              {steps.map((step, i) => (
                <Reveal key={step.title} delay={i * 0.085}>
                  <li className="how-step" style={{ '--n': i } as CSSProperties}>
                    <div className="how-step-head">
                      <span className="how-step-n numeric">{String(i + 1).padStart(2, '0')}</span>
                      <h3>{step.title}</h3>
                    </div>
                    <p className="how-step-copy">{step.body}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Two real screens, not illustrations — the compliance matrix and the
          independent read, captured from the same recording the product film
          is cut from. They reveal on scroll like every other section; nothing
          here animates on its own. */}
      <section className="site-section showcase-section">
        <div className="site-shell">
          <Reveal>
            <SectionMark index={3} total={5} />
            <h2 className="section-title">{t.site.showcase.title}</h2>
          </Reveal>
          <div className="showcase-list">
            {shots.map((shot, i) => (
              <Reveal key={shot.src} delay={i * 0.085}>
                <figure className="showcase-row">
                  <div className="shot-frame">
                    <span className="shot-chrome" aria-hidden="true" />
                    <img src={shot.src} width={1440} height={810} alt={shot.title} loading="lazy" decoding="async" />
                  </div>
                  <figcaption>
                    <h3>{shot.title}</h3>
                    <p>{shot.body}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="site-section features-section">
        <div className="site-shell">
          <Reveal>
            <SectionMark index={4} total={5} />
            <h2 className="section-title">{t.site.features.title}</h2>
          </Reveal>
          <div className="feature-grid">
            {features.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 0.06} className={i === 0 || i === 3 || i === 5 ? 'feature-wide' : undefined}>
                <Spotlight className="feature-card">
                  <h3>
                    <span className="feature-icon">{feature.icon}</span>
                    {feature.title}
                  </h3>
                  <p className="feature-copy">{feature.body}</p>
                </Spotlight>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="site-section audience-section">
        <div className="site-shell">
          <Reveal>
            <SectionMark index={5} total={5} />
            <h2 className="section-title">{t.site.audience.title}</h2>
          </Reveal>
          <div className="audience-grid">
            {audience.map((row, i) => (
              <Reveal key={row.title} delay={i * 0.085}>
                <Spotlight className="audience-card">
                  <span className="audience-icon">{row.icon}</span>
                  <h3>{row.title}</h3>
                </Spotlight>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="site-shell cta-inner">
          <Mark size={72} animateOnView />
          <Reveal>
            <h2>{t.site.cta.title}</h2>
          </Reveal>
          <Reveal delay={0.085}>
            <div className="cta-actions">
              <ButtonLink
                to="/contact"
                variant="primary"
                size="lg"
                iconRight={<ArrowRight size={18} />}
                onClick={() => trackClick('bottom_start', location.pathname)}
              >
                {t.site.cta.primary}
              </ButtonLink>
              {demoButton(t.site.cta.secondary)}
              <ButtonLink to="/contact" variant="ghost" size="lg">
                {t.site.cta.tertiary}
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>
    </SitePage>
  );
}
